import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesAPI, contractsAPI } from '../lib/api';
import { formatDate, formatCurrency, getStatusColor, cn, getInitials, downloadBlob } from '../lib/utils';
import { ArrowLeft, Edit, FileText, Download, History, Briefcase, MapPin, Phone, Mail, Calendar, DollarSign, User, Building, FileCheck, Users, Landmark, Home, Camera, Loader2, HardHat, RefreshCw, Clock, CheckCircle, XCircle, FileSignature, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PPEIssueModal } from '../components/ppe/PPEIssueModal';
import { PPEHistory } from '../components/ppe/PPEHistory';
import { EmployeeAuditLog } from '../components/employee/EmployeeAuditLog';
import { EmployeeLeaveTab } from '../components/employee/EmployeeLeaveTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ContractLeaveModals } from '../components/employee/ContractLeaveModals';
import { AttendanceHistory } from '../components/employee/AttendanceHistory';

const NON_PERMANENT_TYPES = ['Temporary', 'Fixed-Term Contract', 'Contractor', 'Subcontractor', 'Casual/Seasonal'];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [contractHistory, setContractHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPPEModal, setShowPPEModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [renewalData, setRenewalData] = useState({
    new_end_date: '',
    new_start_date: '',
    renewal_reason: '',
    notes: '',
  });
  const [endReason, setEndReason] = useState('Contract ended');
  const [ppeRefreshKey, setPpeRefreshKey] = useState(0);
  const [showEditLeaveModal, setShowEditLeaveModal] = useState(false);
  const [leaveBalanceForm, setLeaveBalanceForm] = useState({
    annual: 0, sick: 0, study: 0, unpaid: 0, reason: '',
  });
  const [savingLeaveBalance, setSavingLeaveBalance] = useState(false);
  const fileInputRef = useRef(null);
  const { isAdmin, isHRManager } = useAuth();
  
  const isNonPermanent = employee && NON_PERMANENT_TYPES.includes(employee.employment_type);

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const [empRes, auditRes] = await Promise.all([
        employeesAPI.getById(id),
        isHRManager() ? employeesAPI.getAuditLog(id) : Promise.resolve({ data: [] }),
      ]);
      setEmployee(empRes.data);
      setAuditLog(auditRes.data);
      
      // Fetch contract history for non-permanent employees
      if (NON_PERMANENT_TYPES.includes(empRes.data.employment_type)) {
        try {
          const historyRes = await employeesAPI.getContractHistory(id);
          setContractHistory(historyRes.data);
        } catch (e) {
          console.error('Failed to fetch contract history:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load employee');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTerminationPdf = async () => {
    try {
      const response = await employeesAPI.getTerminationPdf(id);
      downloadBlob(response.data, `termination_${id}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await employeesAPI.uploadPhoto(id, formData);
      setEmployee(prev => ({ ...prev, avatar: response.data.avatar }));
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleRenewContract = async () => {
    if (!renewalData.new_end_date) {
      toast.error('Please enter the new contract end date');
      return;
    }
    
    try {
      await employeesAPI.renewContract(id, renewalData);
      toast.success('Contract renewed successfully');
      setShowRenewModal(false);
      setRenewalData({ new_end_date: '', new_start_date: '', renewal_reason: '', notes: '' });
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew contract');
    }
  };

  const handleOpenEditLeaveBalance = () => {
    const b = employee?.leave_balance || {};
    setLeaveBalanceForm({
      annual: b.annual ?? 0,
      sick: b.sick ?? 0,
      study: b.study ?? 0,
      unpaid: b.unpaid ?? 0,
      reason: '',
    });
    setShowEditLeaveModal(true);
  };

  const handleSaveLeaveBalance = async () => {
    // Basic validation
    for (const k of ['annual', 'sick', 'study', 'unpaid']) {
      const v = Number(leaveBalanceForm[k]);
      if (isNaN(v) || v < 0) {
        toast.error(`${k[0].toUpperCase() + k.slice(1)} leave must be 0 or greater`);
        return;
      }
    }
    setSavingLeaveBalance(true);
    try {
      await employeesAPI.updateLeaveBalance(id, {
        annual: Number(leaveBalanceForm.annual),
        sick: Number(leaveBalanceForm.sick),
        study: Number(leaveBalanceForm.study),
        unpaid: Number(leaveBalanceForm.unpaid),
        reason: leaveBalanceForm.reason?.trim() || undefined,
      });
      toast.success('Leave balance updated');
      setShowEditLeaveModal(false);
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave balance');
    } finally {
      setSavingLeaveBalance(false);
    }
  };
  
  const handleEndContract = async () => {
    try {
      await employeesAPI.endContract(id, endReason);
      toast.success('Contract ended');
      setShowEndModal(false);
      setEndReason('Contract ended');
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end contract');
    }
  };
  
  const getDaysToExpiry = () => {
    if (!employee?.contract_end_date) return null;
    const today = new Date();
    const endDate = new Date(employee.contract_end_date);
    return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  };
  
  const getContractStatusBadge = () => {
    const days = getDaysToExpiry();
    if (days === null) return null;
    
    if (days < 0) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">Expired ({Math.abs(days)} days ago)</Badge>;
    } else if (days <= 7) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">Expiring in {days} days</Badge>;
    } else if (days <= 14) {
      return <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">Expiring in {days} days</Badge>;
    } else if (days <= 30) {
      return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Expiring in {days} days</Badge>;
    }
    return <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{days} days remaining</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading employee...</div>
      </div>
    );
  }

  if (!employee) return null;

  const isTerminated = employee.status === 'Terminated';

  return (
    <div className="space-y-6 page-transition" data-testid="employee-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
        <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-btn" className="flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-4">
            <div className="relative group flex-shrink-0">
              <Avatar className="w-16 h-16">
                <AvatarImage src={employee.avatar} />
                <AvatarFallback className="bg-primary/20 text-primary text-xl">
                  {getInitials(employee.full_name)}
                </AvatarFallback>
              </Avatar>
              {isHRManager() && !isTerminated && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    data-testid="photo-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid="upload-photo-btn"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </button>
                </>
              )}
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{employee.full_name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-muted-foreground">{employee.job_title}</span>
                <Badge variant="outline" className={cn("border", getStatusColor(employee.status))}>
                  {employee.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isHRManager() && !isTerminated && (
            <Button variant="outline" onClick={() => setShowPPEModal(true)} data-testid="issue-ppe-btn">
              <HardHat className="w-4 h-4 mr-2" />
              Issue PPE
            </Button>
          )}
          {isNonPermanent && isHRManager() && !isTerminated && employee.status !== 'Inactive' && (
            <>
              <Button variant="outline" onClick={() => setShowRenewModal(true)} className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="renew-contract-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Renew
              </Button>
              <Button variant="outline" onClick={() => setShowEndModal(true)} className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="end-contract-btn">
                <XCircle className="w-4 h-4 mr-2" />
                End Contract
              </Button>
            </>
          )}
          {isTerminated && (
            <Button variant="outline" onClick={handleDownloadTerminationPdf} data-testid="download-termination-pdf">
              <Download className="w-4 h-4 mr-2" />
              Termination PDF
            </Button>
          )}
          {isHRManager() && !isTerminated && (
            <Button onClick={() => navigate(`/employees/${id}/edit`)} data-testid="edit-employee-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-6">
        <TabsList className="bg-secondary/30 w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="details">Details</TabsTrigger>
          {isNonPermanent && <TabsTrigger value="contract">Contract</TabsTrigger>}
          <TabsTrigger value="ppe">PPE History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="leave">Leave Balance</TabsTrigger>
          <TabsTrigger value="ta-history" data-testid="ta-history-tab">T&A History</TabsTrigger>
          {isHRManager() && <TabsTrigger value="audit">Audit Log</TabsTrigger>}
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Info */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Personal Information
              </h3>
              <div className="space-y-4">
                <InfoRow icon={Mail} label="Email" value={employee.email} />
                <InfoRow icon={Phone} label="Phone" value={employee.phone} />
                <InfoRow icon={MapPin} label="Physical Address" value={employee.physical_address} />
                <InfoRow icon={Home} label="Residential Address" value={employee.residential_address} />
                <InfoRow icon={Calendar} label="Date of Birth" value={formatDate(employee.date_of_birth)} />
                <InfoRow icon={FileCheck} label="ID Number" value={employee.id_number} />
              </div>
            </div>

            {/* Employment Info */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Employment Details
              </h3>
              <div className="space-y-4">
                <InfoRow icon={Building} label="Department" value={employee.department} />
                <InfoRow icon={Briefcase} label="Job Title" value={employee.job_title} />
                <InfoRow icon={User} label="Line Manager" value={employee.line_manager || '-'} />
                <InfoRow icon={FileText} label="Employment Type" value={employee.employment_type} />
                <InfoRow icon={Calendar} label="Start Date" value={formatDate(employee.start_date)} />
                <InfoRow icon={Calendar} label="Probation End" value={formatDate(employee.probation_end_date)} />
                <InfoRow icon={DollarSign} label="Salary" value={formatCurrency(employee.salary)} />
              </div>
            </div>
          </div>

          {/* Next of Kin & Bank Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Next of Kin */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Next of Kin
              </h3>
              <div className="space-y-4">
                <InfoRow icon={User} label="Name" value={employee.next_of_kin_name} />
                <InfoRow icon={Users} label="Relationship" value={employee.next_of_kin_relationship} />
                <InfoRow icon={Phone} label="Phone" value={employee.next_of_kin_phone} />
              </div>
            </div>

            {/* Bank Account */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Landmark className="w-5 h-5 text-primary" />
                Bank Account Details
              </h3>
              <div className="space-y-4">
                <InfoRow icon={Landmark} label="Bank Name" value={employee.bank_name} />
                <InfoRow icon={FileText} label="Account Number" value={employee.bank_account_number} />
                <InfoRow icon={FileCheck} label="Account Type" value={employee.bank_account_type} />
              </div>
            </div>
          </div>

          {/* Termination Info */}
          {isTerminated && (
            <div className="glass-card p-6 border-destructive/30">
              <h3 className="text-lg font-semibold mb-4 text-destructive">Termination Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Termination Date</p>
                  <p className="font-medium">{formatDate(employee.termination_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <p className="font-medium">{employee.termination_reason}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Final Pay</p>
                  <p className="font-medium">{employee.final_pay_processed ? 'Processed' : 'Pending'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Exit Interview</p>
                  <p className="font-medium">{employee.exit_interview_completed ? 'Completed' : 'Not Done'}</p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Contract Tab (for non-permanent employees) */}
        {isNonPermanent && (
          <TabsContent value="contract" className="space-y-6">
            {/* Current Contract Info */}
            <div className={cn(
              "glass-card p-6",
              getDaysToExpiry() !== null && getDaysToExpiry() < 0 && "border-red-500/30 bg-red-500/5",
              getDaysToExpiry() !== null && getDaysToExpiry() >= 0 && getDaysToExpiry() <= 7 && "border-amber-500/30 bg-amber-500/5"
            )}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-primary" />
                  Current Contract
                </h3>
                {getContractStatusBadge()}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoRow icon={FileText} label="Employment Type" value={employee.employment_type} />
                <InfoRow icon={Calendar} label="Contract Start" value={formatDate(employee.contract_start_date)} />
                <InfoRow icon={Calendar} label="Contract End" value={formatDate(employee.contract_end_date)} />
                <InfoRow icon={Building2} label="Contracting Company" value={employee.contracting_company} />
                <InfoRow icon={User} label="Contract Supervisor" value={employee.contract_supervisor} />
                <InfoRow icon={CheckCircle} label="Renewal Option" value={employee.contract_renewal_option ? 'Yes' : 'No'} />
                <InfoRow icon={Clock} label="Renewal Status" value={employee.renewal_status || 'Not reviewed'} />
                {employee.contract_reason && (
                  <div className="md:col-span-2 lg:col-span-3">
                    <InfoRow icon={FileText} label="Reason for Contract" value={employee.contract_reason} />
                  </div>
                )}
              </div>
              
              {isHRManager() && !isTerminated && employee.status !== 'Inactive' && (
                <div className="flex gap-2 mt-6 pt-4 border-t border-border/50">
                  <Button onClick={() => setShowRenewModal(true)} variant="outline" className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="renew-contract-tab-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renew Contract
                  </Button>
                  <Button onClick={() => setShowEndModal(true)} variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="end-contract-tab-btn">
                    <XCircle className="w-4 h-4 mr-2" />
                    End Contract
                  </Button>
                </div>
              )}
            </div>

            {/* Contract History */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Contract History
              </h3>
              {contractHistory.length > 0 ? (
                <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractHistory.map((record, idx) => (
                      <TableRow key={record.id || idx} className={record.is_current ? 'bg-primary/5' : ''}>
                        <TableCell>
                          {formatDate(record.contract_start_date)} - {formatDate(record.contract_end_date)}
                          {record.is_current && <Badge variant="outline" className="ml-2 text-xs">Current</Badge>}
                        </TableCell>
                        <TableCell>{record.employment_type}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn(
                            "border",
                            record.renewal_status === 'Renewed' && "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
                            record.renewal_status === 'Terminated' && "bg-red-500/20 text-red-400 border-red-500/30",
                            record.renewal_status === 'Expired' && "bg-amber-500/20 text-amber-400 border-amber-500/30",
                            record.renewal_status === 'Active' && "bg-primary/20 text-primary border-primary/30"
                          )}>
                            {record.renewal_status || 'Active'}
                          </Badge>
                        </TableCell>
                        <TableCell>{record.renewed_by_name || record.ended_by_name || '-'}</TableCell>
                        <TableCell>{formatDate(record.renewed_on || record.ended_on)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{record.renewal_reason || record.end_reason || record.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No contract history available
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* PPE History Tab */}
        <TabsContent value="ppe">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <HardHat className="w-5 h-5 text-primary" />
                PPE Issue History
              </h3>
              {isHRManager() && !isTerminated && (
                <Button onClick={() => setShowPPEModal(true)} data-testid="issue-ppe-from-tab-btn">
                  <HardHat className="w-4 h-4 mr-2" />
                  Issue PPE
                </Button>
              )}
            </div>
            <PPEHistory key={ppeRefreshKey} employeeId={employee.employee_id} />
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Documents</h3>
            {employee.documents?.length > 0 ? (
              <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employee.documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.filename}</TableCell>
                      <TableCell>{doc.document_type}</TableCell>
                      <TableCell>{formatDate(doc.expiry_date)}</TableCell>
                      <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No documents uploaded
              </div>
            )}
          </div>
        </TabsContent>

        {/* Leave Balance Tab */}
        <TabsContent value="leave">
          <EmployeeLeaveTab
            employee={employee}
            canEdit={isHRManager() && !isTerminated}
            onEdit={handleOpenEditLeaveBalance}
          />
        </TabsContent>

        {/* Time & Attendance History Tab (lazy — only queries on demand) */}
        <TabsContent value="ta-history">
          <AttendanceHistory employeeId={employee.employee_id} />
        </TabsContent>

        {/* Audit Log Tab */}
        {isHRManager() && (
          <TabsContent value="audit">
            <EmployeeAuditLog auditLog={auditLog} />
          </TabsContent>
        )}
      </Tabs>

      {/* PPE Issue Modal */}
      <PPEIssueModal
        open={showPPEModal}
        onClose={() => setShowPPEModal(false)}
        employee={employee}
        onSuccess={() => setPpeRefreshKey(k => k + 1)}
      />

      <ContractLeaveModals
        employee={employee}
        showRenewModal={showRenewModal} setShowRenewModal={setShowRenewModal}
        renewalData={renewalData} setRenewalData={setRenewalData} handleRenewContract={handleRenewContract}
        showEndModal={showEndModal} setShowEndModal={setShowEndModal}
        endReason={endReason} setEndReason={setEndReason} handleEndContract={handleEndContract}
        showEditLeaveModal={showEditLeaveModal} setShowEditLeaveModal={setShowEditLeaveModal}
        leaveBalanceForm={leaveBalanceForm} setLeaveBalanceForm={setLeaveBalanceForm}
        handleSaveLeaveBalance={handleSaveLeaveBalance} savingLeaveBalance={savingLeaveBalance}
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground text-sm w-32">{label}</span>
      <span className="font-medium text-foreground">{value || '-'}</span>
    </div>
  );
}
